import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { CheckIcon } from "@heroicons/react/24/outline";
import api, { errorCodes } from "../../api";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PatientModuleHeader from "../../components/patients/layout/PatientModuleHeader";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";
import AlertModal from "../../components/AlertModal";
import Modal from "../../components/Modal";
import SelectInput from "../../components/SelectInput";
import TextInput from "../../components/TextInput";
import TextEditor from "../../components/TextEditor";
import Toggle from "../../components/Toggle";
import { FORBIDDEN_MESSAGE } from "./PrintTemplatesPage";

const MEASURES = [
    "pageWidthMm",
    "pageHeightMm",
    "marginTopMm",
    "marginRightMm",
    "marginBottomMm",
    "marginLeftMm",
    "baseFontSizePx",
];
const FIELDS = [
    "pageSize",
    "orientation",
    "preprintedPaper",
    "title",
    "showLogo",
    "showSignature",
    "showInsuranceDefault",
    "headerText",
    "footerText",
    "bodyText",
    ...MEASURES,
];
const FIELD_LABELS = {
    page_size: "Tamaño de papel",
    page_width_mm: "Ancho de página",
    page_height_mm: "Alto de página",
    orientation: "Orientación",
    margin_top_mm: "Margen superior",
    margin_right_mm: "Margen derecho",
    margin_bottom_mm: "Margen inferior",
    margin_left_mm: "Margen izquierdo",
    preprinted_paper: "Papel preimpreso",
    title: "Título",
    show_logo: "Mostrar logo",
    show_signature: "Mostrar firma",
    show_insurance_default: "Mostrar seguro por defecto",
    base_font_size_px: "Tamaño de letra",
    header_text: "Texto de encabezado",
    footer_text: "Texto de pie",
    body_text: "Texto del cuerpo",
};
const ACTION_LABELS = {
    create: "Creación",
    created: "Creación",
    update: "Modificación",
    updated: "Modificación",
    reset: "Valores por defecto",
    restore: "Restauración",
    restored: "Restauración",
};

const toNum = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const isEmptyHtml = (html) =>
    !html || (!/<img/i.test(html) && !html.replace(/<[^>]*>/g, "").trim());

const pick = (form) => Object.fromEntries(FIELDS.map((k) => [k, form[k] ?? null]));

// What the backend receives: numbers for measures, no custom size when not custom
function serialize(form) {
    const out = { ...pick(form) };
    MEASURES.forEach((k) => (out[k] = toNum(out[k])));
    if (out.pageSize !== "custom") {
        out.pageWidthMm = null;
        out.pageHeightMm = null;
    }
    ["headerText", "footerText", "bodyText"].forEach((k) => {
        if (isEmptyHtml(out[k])) out[k] = k === "bodyText" ? out[k] && "" : "";
    });
    return out;
}

function changedFields(form, original) {
    const a = serialize(form);
    const b = serialize(original);
    const textKey = (k) => ["headerText", "footerText", "bodyText"].includes(k);
    return Object.fromEntries(
        FIELDS.filter((k) =>
            textKey(k) ? (a[k] || "") !== (b[k] || "") : a[k] !== b[k]
        ).map((k) => [k, a[k]])
    );
}

function pageDims(form, catalog) {
    let w, h;
    if (form.pageSize === "custom") {
        w = toNum(form.pageWidthMm);
        h = toNum(form.pageHeightMm);
    } else {
        const size = catalog.pageSizes.find((s) => s.value === form.pageSize);
        w = size?.widthMm;
        h = size?.heightMm;
        if (form.orientation === "landscape") [w, h] = [h, w];
    }
    return { w, h };
}

function validate(form, catalog, doc) {
    const { limits } = catalog;
    const errors = {};
    const range = (k, min, max, unit) => {
        const n = toNum(form[k]);
        if (n === null || Number.isNaN(n)) errors[k] = "Campo requerido";
        else if (n < min || n > max)
            errors[k] = `Debe estar entre ${min} y ${max} ${unit}`;
    };
    ["marginTopMm", "marginRightMm", "marginBottomMm", "marginLeftMm"].forEach(
        (k) => range(k, limits.marginMinMm, limits.marginMaxMm, "mm")
    );
    range("baseFontSizePx", limits.fontSizeMinPx, limits.fontSizeMaxPx, "px");
    if (form.pageSize === "custom")
        ["pageWidthMm", "pageHeightMm"].forEach((k) =>
            range(k, limits.pageDimensionMinMm, limits.pageDimensionMaxMm, "mm")
        );
    const { w, h } = pageDims(form, catalog);
    if (w && h) {
        const n = (k) => toNum(form[k]) || 0;
        if (!errors.marginLeftMm && !errors.marginRightMm && n("marginLeftMm") + n("marginRightMm") >= w)
            errors.marginLeftMm = "Los márgenes izquierdo y derecho deben sumar menos que el ancho de la página";
        if (!errors.marginTopMm && !errors.marginBottomMm && n("marginTopMm") + n("marginBottomMm") >= h)
            errors.marginTopMm = "Los márgenes superior e inferior deben sumar menos que el alto de la página";
    }
    if (doc?.value === "doctors_note" && isEmptyHtml(form.bodyText))
        errors.bodyText = "El cuerpo es obligatorio en la constancia de reposo";
    return errors;
}

const firstMessage = (v) => (Array.isArray(v) ? v.join(" ") : String(v));

function Section({ title, children }) {
    return (
        <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {title}
            </h2>
            {children}
        </section>
    );
}

function ToggleRow({ label, hint, value, disabled, onChange }) {
    return (
        <div className={`flex items-center justify-between gap-4 ${disabled ? "opacity-50" : ""}`}>
            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</p>
                {hint && <p className="text-xs text-gray-500">{hint}</p>}
            </div>
            {disabled ? (
                <div className="pointer-events-none">
                    <Toggle value={value} handleChange={() => {}} />
                </div>
            ) : (
                <Toggle value={value} handleChange={onChange} />
            )}
        </div>
    );
}

function PageDiagram({ w, h, form }) {
    if (!w || !h) return null;
    const height = 120;
    const pct = (v, d) => `${Math.min(100, Math.max(0, ((toNum(v) || 0) / d) * 100))}%`;
    return (
        <div
            className="relative shrink-0 border border-gray-400 bg-white dark:border-gray-500"
            style={{ height, width: (height * w) / h }}
            aria-label="Esquema de página con márgenes"
        >
            <div
                className="absolute border border-dashed border-primary bg-primary/10"
                style={{
                    top: pct(form.marginTopMm, h),
                    bottom: pct(form.marginBottomMm, h),
                    left: pct(form.marginLeftMm, w),
                    right: pct(form.marginRightMm, w),
                }}
            />
        </div>
    );
}

function VariablesPanel({ variables, onInsert }) {
    return (
        <details className="-mt-6 mb-6 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
            <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-200">
                Variables disponibles
            </summary>
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                {variables.map((v) => (
                    <li key={v.name}>
                        <button
                            type="button"
                            onClick={() => onInsert(v.name)}
                            className="w-full rounded-md px-2 py-1 text-left hover:bg-gray-200 dark:hover:bg-gray-800"
                        >
                            <code className="text-primary">{`{{ ${v.name} }}`}</code>
                            <span className="block text-xs text-gray-600 dark:text-gray-400">
                                {v.description} — ej.: {v.example}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </details>
    );
}

// ponytail: history item shape (user/date/action keys) is inferred; adjust once the API returns real rows
function HistoryContent({ id, onRestore }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [toRestore, setToRestore] = useState(null);

    useEffect(() => {
        api.printTemplates
            .history(id)
            .then((r) => setRows(r.results ?? r))
            .catch(() => setError("No se pudo cargar el historial"));
    }, [id]);

    const fields = (row) => {
        const raw = row.changedFields;
        const list = Array.isArray(raw) ? raw : raw ? Object.keys(raw) : [];
        return list.map((f) => FIELD_LABELS[f]).filter(Boolean);
    };
    const who = (row) => {
        const u = row.user ?? row.changedBy ?? row.historyUser ?? row.updatedBy;
        return u && typeof u === "object"
            ? u.fullName || [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email
            : u;
    };

    return (
        <div>
            <h3 className="mb-3 text-lg font-semibold">Historial</h3>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {!rows && !error && <p className="text-sm text-gray-500">Cargando...</p>}
            {rows && rows.length === 0 && (
                <p className="text-sm text-gray-500">Todavía no hay cambios registrados.</p>
            )}
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows?.map((row) => {
                    const date = row.historyDate ?? row.createdAt ?? row.date ?? row.updatedAt;
                    const action = row.action ?? row.historyType;
                    return (
                        <li key={row.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                            <div className="min-w-0 flex-1 text-sm">
                                <p className="font-medium">
                                    {date && new Date(date).toLocaleString("es-PY")} · {who(row) || "—"}
                                </p>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {ACTION_LABELS[action] ?? action}
                                    {fields(row).length > 0 && `: ${fields(row).join(", ")}`}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setToRestore(row)}
                                className="rounded-lg px-3 py-1.5 text-sm font-semibold text-primary ring-1 ring-primary"
                            >
                                Restaurar esta versión
                            </button>
                        </li>
                    );
                })}
            </ul>
            <AlertModal
                open={!!toRestore}
                onClose={() => setToRestore(null)}
                title="Restaurar esta versión"
                messages={["El documento volverá a la configuración de esta versión."]}
                confirmLabel="Restaurar"
                onConfirm={() => {
                    const row = toRestore;
                    setToRestore(null);
                    onRestore(row.id);
                }}
            />
        </div>
    );
}

export default function PrintTemplateEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const [catalog, setCatalog] = useState(null);
    const [original, setOriginal] = useState(null);
    const [form, setForm] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [serverErrors, setServerErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [modal, setModal] = useState(null); // "reset" | "leave"
    const [historyOpen, setHistoryOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [preview, setPreview] = useState({ loading: false, error: null });
    const reqId = useRef(0);
    const urlRef = useRef(null);
    const editors = {
        headerText: useRef(),
        footerText: useRef(),
        bodyText: useRef(),
    };

    const doc = catalog?.documentTypes.find((d) => d.value === original?.documentType);
    const clientErrors = form && catalog ? validate(form, catalog, doc) : {};
    const errorOf = (k) => clientErrors[k] || (serverErrors[k] && firstMessage(serverErrors[k]));
    const changes = form && original ? changedFields(form, original) : {};
    const dirty = Object.keys(changes).length > 0;

    const runPreview = useCallback(async (request) => {
        const mine = ++reqId.current;
        setPreview({ loading: true, error: null });
        try {
            const blob = await request();
            if (mine !== reqId.current) return;
            if (urlRef.current) URL.revokeObjectURL(urlRef.current);
            urlRef.current = URL.createObjectURL(blob);
            setPdfUrl(urlRef.current);
            setPreview({ loading: false, error: null });
        } catch (e) {
            if (mine !== reqId.current) return;
            let error = "Ocurrió un error al generar la vista previa";
            if (e.status === 400) {
                error = "Hay errores en el formulario";
                if (e.message && typeof e.message === "object") setServerErrors(e.message);
            } else if (e.status === 504) {
                error = "La vista previa tardó demasiado, probá de nuevo";
            }
            setPreview({ loading: false, error });
        }
    }, []);

    const previewForm = useCallback(
        (f) =>
            runPreview(() =>
                api.printTemplates.preview({ documentType: f.documentType, ...serialize(f) })
            ),
        [runPreview]
    );
    const previewSaved = useCallback(
        () => runPreview(() => api.printTemplates.previewSaved(id)),
        [runPreview, id]
    );

    const load = useCallback(async () => {
        const t = await api.printTemplates.get(id);
        setOriginal(t);
        setForm(t);
        setServerErrors({});
        return t;
    }, [id]);

    useEffect(() => {
        Promise.all([api.printTemplates.getCatalog(), load()])
            .then(([cat]) => {
                setCatalog(cat);
                previewSaved();
            })
            .catch((e) =>
                setLoadError(
                    e.code === errorCodes.forbidden
                        ? FORBIDDEN_MESSAGE
                        : "No se pudo cargar el template"
                )
            );
    }, [load, previewSaved]);

    useEffect(() => {
        const ids = reqId;
        return () => {
            ids.current++;
            if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        };
    }, []);

    // Optional auto preview, debounced; only with a valid, modified form
    const hasClientErrors = Object.keys(clientErrors).length > 0;
    useEffect(() => {
        if (!form || !dirty || hasClientErrors) return;
        const t = setTimeout(() => previewForm(form), 1500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);

    useEffect(() => {
        if (!dirty) return;
        const warn = (e) => e.preventDefault();
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);

    const handleChange = (name, value) => {
        if (["headerText", "footerText", "bodyText"].includes(name) && value === (form[name] ?? "")) return;
        setForm((f) => ({ ...f, [name]: value }));
        setServerErrors((s) => {
            if (!s[name]) return s;
            const { [name]: _, ...rest } = s;
            return rest;
        });
    };

    const insertVariable = (field, name) => {
        const editor = editors[field].current?.getEditor();
        if (!editor) return;
        const at = editor.getSelection(true)?.index ?? editor.getLength() - 1;
        editor.insertText(at, `{{ ${name} }}`, "user");
    };

    const save = async () => {
        if (hasClientErrors) return toast.error("Corregí los errores del formulario");
        setSaving(true);
        try {
            const saved = await api.printTemplates.patch(id, changes);
            setOriginal(saved);
            setForm(saved);
            setServerErrors({});
            toast.success("Cambios guardados");
        } catch (e) {
            if (e.code === errorCodes.badRequest && typeof e.message === "object")
                setServerErrors(e.message);
            toast.error(
                e.code === errorCodes.badRequest
                    ? "Hay errores en el formulario"
                    : "No se pudieron guardar los cambios"
            );
        } finally {
            setSaving(false);
        }
    };

    const reloadAndPreview = async (okMessage) => {
        await load();
        toast.success(okMessage);
        previewSaved();
    };

    const reset = async () => {
        setModal(null);
        try {
            await api.printTemplates.reset(id);
            await reloadAndPreview("Valores por defecto restaurados");
        } catch (e) {
            toast.error("No se pudo restaurar el documento");
        }
    };

    const restore = async (historyId) => {
        try {
            await api.printTemplates.restore(id, historyId);
            setHistoryOpen(false);
            await reloadAndPreview("Versión restaurada");
        } catch (e) {
            toast.error("No se pudo restaurar la versión");
        }
    };

    const goBack = () =>
        dirty ? setModal("leave") : navigate("/settings/print-templates");

    const handleSidebarToggle = () =>
        isDesktop ? setSidebarOpenDesktop(!sidebarOpenDesktop) : setSidebarOpen(true);

    const pages = [
        { name: "Templates de impresión", href: "#", current: false },
        { name: doc?.label || "Editar", href: "#", current: true },
    ];
    const { w, h } = form && catalog ? pageDims(form, catalog) : {};
    const textFields = [
        ["headerText", "Texto de encabezado"],
        ["footerText", "Texto de pie"],
        ...(doc?.supportsBodyText ? [["bodyText", "Texto del cuerpo"]] : []),
    ];

    return (
        <PatientModuleLayout
            currentScreen="Templates de impresión"
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            header={
                <PatientModuleHeader
                    pages={pages}
                    onToggleSidebar={handleSidebarToggle}
                    mobileBackTo="/settings/print-templates"
                    mobileBackLabel="Templates"
                    mobileTitle={doc?.label || "Editar"}
                    actions={
                        <>
                            <button
                                type="button"
                                onClick={goBack}
                                className="rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 dark:text-gray-200 dark:ring-gray-600"
                            >
                                Volver
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={!dirty || saving}
                                className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
                            >
                                <CheckIcon className="h-5 w-5" />
                                {saving ? "Guardando..." : "Guardar"}
                            </button>
                        </>
                    }
                />
            }
        >
            <Toaster />
            {loadError && (
                <p className="m-4 rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    {loadError}
                </p>
            )}
            {!form && !loadError && <p className="p-6 text-sm text-gray-500">Cargando...</p>}
            {form && catalog && (
                <div className="grid gap-4 p-4 lg:grid-cols-2 lg:p-6">
                    <div className="space-y-4">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {doc?.label}
                        </h1>

                        <Section title="Papel">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <SelectInput
                                    name="pageSize"
                                    label="Tamaño de papel"
                                    parse={false}
                                    value={form.pageSize}
                                    handleChange={handleChange}
                                    errorMessage={errorOf("pageSize")}
                                    options={catalog.pageSizes.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                />
                                <SelectInput
                                    name="orientation"
                                    label="Orientación"
                                    parse={false}
                                    value={form.orientation}
                                    handleChange={handleChange}
                                    errorMessage={errorOf("orientation")}
                                    options={catalog.orientations.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                />
                            </div>
                            {form.pageSize === "custom" ? (
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {[["pageWidthMm", "Ancho (mm)"], ["pageHeightMm", "Alto (mm)"]].map(([k, l]) => (
                                        <TextInput
                                            key={k}
                                            type="number"
                                            name={k}
                                            label={l}
                                            min={catalog.limits.pageDimensionMinMm}
                                            max={catalog.limits.pageDimensionMaxMm}
                                            value={form[k]}
                                            handleChange={handleChange}
                                            errorMessage={errorOf(k)}
                                            showErrorMessage
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Medidas: {w} × {h} mm
                                </p>
                            )}
                            <ToggleRow
                                label="Papel preimpreso"
                                value={form.preprintedPaper}
                                onChange={(v) => handleChange("preprintedPaper", v)}
                            />
                        </Section>

                        <Section title="Márgenes (mm)">
                            <div className="flex items-start gap-4">
                                <PageDiagram w={w} h={h} form={form} />
                                <div className="grid flex-1 grid-cols-2 gap-3">
                                    {[
                                        ["marginTopMm", "Superior"],
                                        ["marginRightMm", "Derecho"],
                                        ["marginBottomMm", "Inferior"],
                                        ["marginLeftMm", "Izquierdo"],
                                    ].map(([k, l]) => (
                                        <TextInput
                                            key={k}
                                            type="number"
                                            name={k}
                                            label={l}
                                            min={catalog.limits.marginMinMm}
                                            max={catalog.limits.marginMaxMm}
                                            value={form[k]}
                                            handleChange={handleChange}
                                            errorMessage={errorOf(k)}
                                            showErrorMessage
                                        />
                                    ))}
                                </div>
                            </div>
                        </Section>

                        <Section title="Contenido">
                            <TextInput
                                name="title"
                                label="Título"
                                value={form.title}
                                handleChange={handleChange}
                                errorMessage={errorOf("title")}
                                showErrorMessage
                            />
                            <TextInput
                                type="number"
                                name="baseFontSizePx"
                                label="Tamaño de letra (px)"
                                min={catalog.limits.fontSizeMinPx}
                                max={catalog.limits.fontSizeMaxPx}
                                value={form.baseFontSizePx}
                                handleChange={handleChange}
                                errorMessage={errorOf("baseFontSizePx")}
                                showErrorMessage
                            />
                            <ToggleRow
                                label="Mostrar logo"
                                hint={form.preprintedPaper ? "El papel preimpreso ya trae el logo" : null}
                                value={form.showLogo}
                                disabled={form.preprintedPaper}
                                onChange={(v) => handleChange("showLogo", v)}
                            />
                            <ToggleRow
                                label="Mostrar firma"
                                value={form.showSignature}
                                onChange={(v) => handleChange("showSignature", v)}
                            />
                            <ToggleRow
                                label="Mostrar seguro por defecto"
                                value={form.showInsuranceDefault}
                                onChange={(v) => handleChange("showInsuranceDefault", v)}
                            />
                        </Section>

                        <Section title="Textos">
                            {textFields.map(([k, label]) => (
                                <div key={k}>
                                    <TextEditor
                                        simple
                                        name={k}
                                        label={label}
                                        required={k === "bodyText" && doc.value === "doctors_note"}
                                        value={form[k]}
                                        handleChange={handleChange}
                                        editorRef={editors[k]}
                                        height={160}
                                        error={errorOf(k)}
                                    />
                                    <VariablesPanel
                                        variables={doc.variables}
                                        onInsert={(name) => insertVariable(k, name)}
                                    />
                                </div>
                            ))}
                        </Section>

                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setHistoryOpen(true)}
                                className="rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 dark:text-gray-200 dark:ring-gray-600"
                            >
                                Historial
                            </button>
                            <button
                                type="button"
                                onClick={() => setModal("reset")}
                                className="rounded-xl px-4 py-2 text-sm font-semibold text-red-600 ring-1 ring-red-300"
                            >
                                Restaurar valores por defecto
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2 lg:sticky lg:top-24 lg:self-start">
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Vista previa con datos de ejemplo
                            </p>
                            <button
                                type="button"
                                onClick={() => previewForm(form)}
                                disabled={preview.loading || hasClientErrors}
                                className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
                            >
                                Actualizar vista previa
                            </button>
                        </div>
                        {preview.error && (
                            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
                                {preview.error}
                            </p>
                        )}
                        <div className="relative h-[70vh] overflow-hidden rounded-xl border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800">
                            {pdfUrl && (
                                <iframe title="Vista previa" src={pdfUrl} className="h-full w-full" />
                            )}
                            {preview.loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/60 text-sm dark:bg-gray-900/60">
                                    Generando vista previa...
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Modal
                open={historyOpen}
                setOpen={setHistoryOpen}
                size="sm:max-w-2xl"
                content={<HistoryContent id={id} onRestore={restore} />}
            />
            <AlertModal
                open={modal === "reset"}
                onClose={() => setModal(null)}
                title="Restaurar valores por defecto"
                messages={[
                    "Se perderán los cambios y el documento volverá a imprimirse como originalmente. Podés recuperar la versión anterior desde el historial.",
                ]}
                confirmLabel="Restaurar"
                onConfirm={reset}
            />
            <AlertModal
                open={modal === "leave"}
                onClose={() => setModal(null)}
                title="Cambios sin guardar"
                messages={["Si salís ahora, perderás los cambios que no guardaste."]}
                confirmLabel="Salir"
                onConfirm={() => navigate("/settings/print-templates")}
            />
        </PatientModuleLayout>
    );
}
